import { z } from "zod";
import { PRODUCTS } from "./common.ts";

/**
 * The request of the provisioning Job of a customer installation (`strust-server
 * provision-customer`, change adopt-installation-per-customer design D14 item 2): what the
 * control plane renders into the Job's ConfigMap (control-plane-customer.mjs, task 6.12). Unknown
 * keys are refused.
 */
const PROVISIONING_RUN_ID = z.string().regex(/^[A-Za-z0-9._-]{1,80}$/);
const PROVISIONING_EMAIL = z
  .string()
  .max(254)
  .regex(/^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,}$/);
const CONSOLE_USER_ID = z.uuid();

/**
 * One Administrator action of S-Trust's Fleet Administrators panel (change
 * adopt-installation-per-customer, design D15 item 4), run by the Job as the system (cause
 * `s_trust_support`).
 */
export const FleetAdministratorActionV1 = z.discriminatedUnion("action", [
  z.object({ action: z.literal("list-administrators") }).strict(),
  z.object({ action: z.literal("add-administrator"), email: PROVISIONING_EMAIL }).strict(),
  z.object({ action: z.literal("deactivate-administrator"), console_user_id: CONSOLE_USER_ID }).strict(),
  z.object({ action: z.literal("reactivate-administrator"), console_user_id: CONSOLE_USER_ID }).strict(),
  z.object({ action: z.literal("resend-temporary-password"), console_user_id: CONSOLE_USER_ID }).strict(),
]);
export type FleetAdministratorActionV1 = z.infer<typeof FleetAdministratorActionV1>;

const PROVISIONING_OPERATOR_SLUG = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/);
const PROVISIONING_PRODUCTS = z
  .array(z.enum(PRODUCTS))
  .min(1)
  .max(PRODUCTS.length)
  .refine((list) => new Set(list).size === list.length, "products repeat");

/**
 * The operators part of a customer's `configure` request (design D14 item 4, D15 item 1: later
 * additions and deactivations of operators stay with the control plane): new operators with their
 * products, more products on existing operators, deactivations with their reason. Run by the Job as
 * the system (cause `configure`). At least one entry; a slug at most once.
 */
export const FleetOperatorsActionV1 = z
  .object({
    add: z
      .array(z.object({ slug: PROVISIONING_OPERATOR_SLUG, name: z.string().trim().min(1).max(80), products: PROVISIONING_PRODUCTS }).strict())
      .max(20)
      .optional(),
    enable_products: z
      .array(z.object({ slug: PROVISIONING_OPERATOR_SLUG, products: PROVISIONING_PRODUCTS }).strict())
      .max(20)
      .optional(),
    deactivate: z
      .array(z.object({ slug: PROVISIONING_OPERATOR_SLUG, reason: z.string().trim().min(1).max(500) }).strict())
      .max(20)
      .optional(),
  })
  .strict()
  .refine(
    (a) => (a.add?.length ?? 0) + (a.enable_products?.length ?? 0) + (a.deactivate?.length ?? 0) > 0,
    "at least one operator change",
  )
  .refine((a) => {
    const slugs = [...(a.add ?? []), ...(a.enable_products ?? []), ...(a.deactivate ?? [])].map((o) => o.slug);
    return new Set(slugs).size === slugs.length;
  }, "an operator is named once");
export type FleetOperatorsActionV1 = z.infer<typeof FleetOperatorsActionV1>;

/**
 * A `configure` request of the provisioning Job (design D15 item 4): one Administrator action or
 * one operators action, run after a read-only `--check` pass. Unknown keys are refused.
 */
export const ProvisioningConfigureRequestV1 = z
  .object({
    version: z.literal(1),
    run_id: PROVISIONING_RUN_ID,
    /** The control plane request (`REQUEST#<id>`) behind the run (audit context). */
    request_id: z.string().regex(/^[A-Za-z0-9._-]{1,80}$/),
    operation: z.literal("configure"),
    administrator_action: FleetAdministratorActionV1.optional(),
    operators_action: FleetOperatorsActionV1.optional(),
  })
  .strict()
  .refine((r) => (r.administrator_action === undefined) !== (r.operators_action === undefined), "exactly one action")
  .meta({ id: "ProvisioningConfigureRequestV1" });
export type ProvisioningConfigureRequestV1 = z.infer<typeof ProvisioningConfigureRequestV1>;

export const ProvisioningRequestV1 = z
  .object({
    version: z.literal(1),
    /** The control plane run that rendered it (audit context). */
    run_id: z.string().regex(/^[A-Za-z0-9._-]{1,80}$/),
    operation: z.literal("provision"),
    /** The customer's Organization in the installation. */
    organization_name: z.string().trim().min(1).max(80),
    /** The customer's operators with their contracted products (the same list in every installation). */
    operators: z
      .array(
        z
          .object({
            slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/),
            name: z.string().trim().min(1).max(80),
            products: z
              .array(z.enum(PRODUCTS))
              .min(1)
              .max(PRODUCTS.length)
              .refine((list) => new Set(list).size === list.length, "products repeat"),
          })
          .strict(),
      )
      .min(1)
      .max(20),
    /** The customer's first Administrator's e-mail (the `administrator` phase). */
    admin_email: z
      .string()
      .max(254)
      .regex(/^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,}$/)
      .optional(),
  })
  .strict()
  .meta({ id: "ProvisioningRequestV1" });
export type ProvisioningRequestV1 = z.infer<typeof ProvisioningRequestV1>;
